// src/pages/trading-portal/AIAnalysisEngine/MarketRegimeDetectionPage.js
// Market Regime Detection - tabbed interface with single "Run All Analysis" button

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Grid,
    Chip,
    CircularProgress,
    Tabs,
    Tab,
    Skeleton,
    Card,
    CardContent,
    LinearProgress,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    BubbleChart as BubbleChartIcon,
    PlaylistPlay as PlaylistPlayIcon,
    ShowChart as ShowChartIcon,
    CompareArrows as CompareArrowsIcon,
    TrendingUp as TrendingUpIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    MarketRegimeTypeLabels,
    MarketRegimeTypeColors,
    VolRegimeTypeLabels,
    VolRegimeTypeColors,
    TrendDirectionLabels,
    TrendDirectionColors,
    TailRiskLevelLabels,
    TailRiskLevelColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';
const thSx = { fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' };
const tdSx = { fontFamily, color: '#64748b', fontSize: '0.8rem' };
const tdBoldSx = { fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' };
const PER_PAGE = 10;

const MarketRegimeDetectionPage = () => {
    const [canView, setCanView] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    // Summary data
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [currentRegime, setCurrentRegime] = useState(null);
    const [regimeHistory, setRegimeHistory] = useState([]);
    const [regimeTotal, setRegimeTotal] = useState(0);
    const [regimePage, setRegimePage] = useState(0);
    const [volRegimes, setVolRegimes] = useState([]);
    const [volTotal, setVolTotal] = useState(0);
    const [volPage, setVolPage] = useState(0);
    const [correlations, setCorrelations] = useState([]);
    const [corrTotal, setCorrTotal] = useState(0);
    const [corrPage, setCorrPage] = useState(0);
    const [trendData, setTrendData] = useState([]);
    const [trendTotal, setTrendTotal] = useState(0);
    const [trendPage, setTrendPage] = useState(0);
    const [tailRisks, setTailRisks] = useState([]);
    const [tailTotal, setTailTotal] = useState(0);
    const [tailPage, setTailPage] = useState(0);

    // Run all
    const [runningAll, setRunningAll] = useState(false);

    // Permissions
    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-aae-regime-detection', 'view');
            setCanView(view);
        };
        checkPermissions();
    }, []);

    // Fetch summary
    const fetchSummary = useCallback(async () => {
        if (!canView) return;
        setSummaryLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                per_page: PER_PAGE,
                regime_page: regimePage + 1,
                vol_page: volPage + 1,
                corr_page: corrPage + 1,
                trend_page: trendPage + 1,
                tail_page: tailPage + 1,
            });
            const response = await axiosInstance.get(`/api/market-regime-detection/summary?${params.toString()}`);
            if (response.data.success) {
                const d = response.data.data;
                setCurrentRegime(d.current_regime);
                setRegimeHistory(d.regime_history.data || []);
                setRegimeTotal(d.regime_history.total || 0);
                setVolRegimes(d.vol_regimes.data || []);
                setVolTotal(d.vol_regimes.total || 0);
                setCorrelations(d.correlations.data || []);
                setCorrTotal(d.correlations.total || 0);
                setTrendData(d.trend_reversion.data || []);
                setTrendTotal(d.trend_reversion.total || 0);
                setTailRisks(d.tail_risk.data || []);
                setTailTotal(d.tail_risk.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch summary');
        } finally {
            setSummaryLoading(false);
        }
    }, [canView, regimePage, volPage, corrPage, trendPage, tailPage]);

    useEffect(() => {
        if (canView) fetchSummary();
    }, [fetchSummary, canView]);

    // Run All Analysis
    const handleRunAll = async () => {
        setRunningAll(true);
        setError(null);
        setSuccessMsg(null);
        try {
            const response = await axiosInstance.post('/api/market-regime-detection/run-all', {}, {
                timeout: 600000
            });
            if (response.data.success) {
                const r = response.data.data;
                setSuccessMsg(
                    `Analysis complete: Regime detected, ${r.vol_regimes_classified} vol regimes, ${r.correlations_analyzed} correlations, ${r.trends_detected} trends, ${r.tail_risks_estimated} tail risks.` +
                    (r.errors.length > 0 ? ` (${r.errors.length} errors)` : '')
                );
                setTimeout(() => setSuccessMsg(null), 8000);
                fetchSummary();
            } else {
                setError(response.data.message || 'Analysis failed');
            }
        } catch (err) {
            if (err.code === 'ECONNABORTED') {
                setError('Analysis timed out. It may still be running in the background.');
            } else {
                setError(err.response?.data?.message || 'Analysis failed');
            }
        } finally {
            setRunningAll(false);
        }
    };

    // Render: Regime Detection tab
    const renderRegimeDetection = () => (
        <Box>
            {currentRegime && (
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem', mb: 2 }}>
                            Current Market Regime
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Chip
                                        label={MarketRegimeTypeLabels[currentRegime.detected_regime] || currentRegime.detected_regime}
                                        color={MarketRegimeTypeColors[currentRegime.detected_regime] || 'default'}
                                        sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', height: 36, px: 2 }}
                                    />
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', mt: 1 }}>Regime Type</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ fontFamily, fontWeight: 800, color: '#1e293b', fontSize: '1.5rem' }}>
                                        {currentRegime.regime_probability != null ? `${(currentRegime.regime_probability * 100).toFixed(1)}%` : '--'}
                                    </Typography>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Confidence</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ fontFamily, fontWeight: 800, color: '#1e293b', fontSize: '1.5rem' }}>
                                        {currentRegime.regime_duration_days || '--'}
                                    </Typography>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Days in Regime</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={thSx}>Regime</TableCell>
                                <TableCell sx={thSx}>Confidence</TableCell>
                                <TableCell sx={thSx}>Transition Prob</TableCell>
                                <TableCell sx={thSx}>Duration (days)</TableCell>
                                <TableCell sx={thSx}>Detection Date</TableCell>
                                <TableCell sx={thSx}>Previous Regime</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {regimeHistory.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No regime history. Click "Run All Analysis" to get started.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : regimeHistory.map((regime) => (
                                <TableRow key={regime.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                    <TableCell>
                                        <Chip label={MarketRegimeTypeLabels[regime.detected_regime] || regime.detected_regime} size="small"
                                            color={MarketRegimeTypeColors[regime.detected_regime] || 'default'}
                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                    </TableCell>
                                    <TableCell sx={tdBoldSx}>{regime.regime_probability != null ? `${(regime.regime_probability * 100).toFixed(1)}%` : '--'}</TableCell>
                                    <TableCell sx={tdSx}>{regime.transition_probability != null ? `${(regime.transition_probability * 100).toFixed(1)}%` : '--'}</TableCell>
                                    <TableCell sx={tdSx}>{regime.regime_duration_days || '--'}</TableCell>
                                    <TableCell sx={{ ...tdSx, color: '#94a3b8', fontSize: '0.75rem' }}>{regime.detection_date ? new Date(regime.detection_date).toLocaleDateString() : '--'}</TableCell>
                                    <TableCell>
                                        {regime.previous_regime ? (
                                            <Chip label={MarketRegimeTypeLabels[regime.previous_regime] || regime.previous_regime} size="small"
                                                variant="outlined" color={MarketRegimeTypeColors[regime.previous_regime] || 'default'}
                                                sx={{ fontFamily, fontWeight: 500, fontSize: '0.65rem', height: 22 }} />
                                        ) : '--'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination component="div" count={regimeTotal} page={regimePage}
                    onPageChange={(e, p) => setRegimePage(p)} rowsPerPage={PER_PAGE} rowsPerPageOptions={[PER_PAGE]}
                    sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
            </Paper>
        </Box>
    );

    // Render: Vol Regime tab
    const renderVolRegime = () => (
        <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={thSx}>Symbol</TableCell>
                            <TableCell sx={thSx}>Vol Regime</TableCell>
                            <TableCell sx={thSx}>Vol of Vol</TableCell>
                            <TableCell sx={thSx}>Percentile</TableCell>
                            <TableCell sx={thSx}>Term Structure</TableCell>
                            <TableCell sx={thSx}>Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {volRegimes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No vol regime data. Click "Run All Analysis" to classify.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : volRegimes.map((vr) => (
                            <TableRow key={vr.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                <TableCell sx={tdBoldSx}>{vr.symbol_name || vr.symbol || '--'}</TableCell>
                                <TableCell>
                                    <Chip label={VolRegimeTypeLabels[vr.vol_regime] || vr.vol_regime} size="small"
                                        color={VolRegimeTypeColors[vr.vol_regime] || 'default'}
                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                </TableCell>
                                <TableCell sx={tdBoldSx}>{vr.vol_of_vol != null ? `${(vr.vol_of_vol * 100).toFixed(2)}%` : '--'}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LinearProgress variant="determinate" value={vr.vol_percentile != null ? vr.vol_percentile : 0}
                                            sx={{ width: 60, height: 6, borderRadius: 3, bgcolor: '#e2e8f0',
                                                '& .MuiLinearProgress-bar': { bgcolor: vr.vol_percentile > 80 ? '#ef4444' : vr.vol_percentile > 50 ? ACCENT : '#10b981', borderRadius: 3 } }} />
                                        <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.8rem' }}>
                                            {vr.vol_percentile != null ? `${vr.vol_percentile.toFixed(0)}%` : '--'}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={tdSx}>{vr.term_structure_slope != null ? `${(vr.term_structure_slope * 100).toFixed(2)}%` : '--'}</TableCell>
                                <TableCell sx={{ ...tdSx, color: '#94a3b8', fontSize: '0.75rem' }}>{vr.classification_date ? new Date(vr.classification_date).toLocaleDateString() : '--'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination component="div" count={volTotal} page={volPage}
                onPageChange={(e, p) => setVolPage(p)} rowsPerPage={PER_PAGE} rowsPerPageOptions={[PER_PAGE]}
                sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
        </Paper>
    );

    // Render: Correlations tab
    const renderCorrelations = () => (
        <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={thSx}>Asset Pair</TableCell>
                            <TableCell sx={thSx}>Correlation (30d)</TableCell>
                            <TableCell sx={thSx}>Corr (60d)</TableCell>
                            <TableCell sx={thSx}>Corr (90d)</TableCell>
                            <TableCell sx={thSx}>Change (30d)</TableCell>
                            <TableCell sx={thSx}>Breakdown Alert</TableCell>
                            <TableCell sx={thSx}>Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {correlations.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No correlation data. Click "Run All Analysis" to monitor.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : correlations.map((corr) => (
                            <TableRow key={corr.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                <TableCell sx={tdBoldSx}>{corr.asset_a_symbol || '--'} / {corr.asset_b_symbol || '--'}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                                            bgcolor: corr.correlation_30d > 0.7 ? '#10b981' : corr.correlation_30d > 0.3 ? ACCENT : corr.correlation_30d > -0.3 ? '#94a3b8' : '#ef4444' }} />
                                        <Typography sx={tdBoldSx}>{corr.correlation_30d != null ? corr.correlation_30d.toFixed(4) : '--'}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={tdSx}>{corr.correlation_60d != null ? corr.correlation_60d.toFixed(4) : '--'}</TableCell>
                                <TableCell sx={tdSx}>{corr.correlation_90d != null ? corr.correlation_90d.toFixed(4) : '--'}</TableCell>
                                <TableCell sx={{ ...tdSx, color: Math.abs(corr.correlation_change_30d || 0) > 0.2 ? '#ef4444' : '#64748b', fontWeight: Math.abs(corr.correlation_change_30d || 0) > 0.2 ? 600 : 400 }}>
                                    {corr.correlation_change_30d != null ? corr.correlation_change_30d.toFixed(4) : '--'}
                                </TableCell>
                                <TableCell>
                                    {corr.is_breakdown_alert ? (
                                        <Chip label="BREAKDOWN" size="small" color="error" sx={{ fontFamily, fontWeight: 700, fontSize: '0.65rem', height: 22 }} />
                                    ) : (
                                        <Chip label="Normal" size="small" color="default" variant="outlined" sx={{ fontFamily, fontWeight: 500, fontSize: '0.65rem', height: 22 }} />
                                    )}
                                </TableCell>
                                <TableCell sx={{ ...tdSx, color: '#94a3b8', fontSize: '0.75rem' }}>{corr.monitor_date ? new Date(corr.monitor_date).toLocaleDateString() : '--'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination component="div" count={corrTotal} page={corrPage}
                onPageChange={(e, p) => setCorrPage(p)} rowsPerPage={PER_PAGE} rowsPerPageOptions={[PER_PAGE]}
                sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
        </Paper>
    );

    // Render: Trend/Reversion tab
    const renderTrendReversion = () => (
        <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={thSx}>Symbol</TableCell>
                            <TableCell sx={thSx}>Direction</TableCell>
                            <TableCell sx={thSx}>Strength</TableCell>
                            <TableCell sx={thSx}>Mean Rev Score</TableCell>
                            <TableCell sx={thSx}>Half-Life</TableCell>
                            <TableCell sx={thSx}>Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {trendData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No trend data. Click "Run All Analysis" to detect.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : trendData.map((td) => (
                            <TableRow key={td.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                <TableCell sx={tdBoldSx}>{td.symbol_name || td.symbol || '--'}</TableCell>
                                <TableCell>
                                    <Chip label={TrendDirectionLabels[td.trend_direction] || td.trend_direction} size="small"
                                        color={TrendDirectionColors[td.trend_direction] || 'default'}
                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LinearProgress variant="determinate" value={td.trend_strength != null ? td.trend_strength * 100 : 0}
                                            sx={{ width: 50, height: 5, borderRadius: 3, bgcolor: '#e2e8f0',
                                                '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 3 } }} />
                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                            {td.trend_strength != null ? `${(td.trend_strength * 100).toFixed(0)}%` : '--'}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={tdSx}>{td.mean_reversion_score != null ? td.mean_reversion_score.toFixed(4) : '--'}</TableCell>
                                <TableCell sx={tdSx}>{td.half_life_days != null ? `${td.half_life_days.toFixed(1)} days` : '--'}</TableCell>
                                <TableCell sx={{ ...tdSx, color: '#94a3b8', fontSize: '0.75rem' }}>{td.detection_date ? new Date(td.detection_date).toLocaleDateString() : '--'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination component="div" count={trendTotal} page={trendPage}
                onPageChange={(e, p) => setTrendPage(p)} rowsPerPage={PER_PAGE} rowsPerPageOptions={[PER_PAGE]}
                sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
        </Paper>
    );

    // Render: Tail Risk tab
    const renderTailRisk = () => (
        <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={thSx}>Symbol</TableCell>
                            <TableCell sx={thSx}>Risk Level</TableCell>
                            <TableCell sx={thSx}>VaR (95%)</TableCell>
                            <TableCell sx={thSx}>VaR (99%)</TableCell>
                            <TableCell sx={thSx}>CVaR (95%)</TableCell>
                            <TableCell sx={thSx}>CVaR (99%)</TableCell>
                            <TableCell sx={thSx}>Skewness</TableCell>
                            <TableCell sx={thSx}>Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tailRisks.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No tail risk data. Click "Run All Analysis" to estimate.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : tailRisks.map((tr) => (
                            <TableRow key={tr.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                <TableCell sx={tdBoldSx}>{tr.symbol_name || tr.symbol || '--'}</TableCell>
                                <TableCell>
                                    <Chip label={TailRiskLevelLabels[tr.risk_level] || tr.risk_level} size="small"
                                        color={TailRiskLevelColors[tr.risk_level] || 'default'}
                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                </TableCell>
                                <TableCell sx={{ ...tdBoldSx, color: '#ef4444' }}>{tr.var_95 != null ? `${(tr.var_95 * 100).toFixed(2)}%` : '--'}</TableCell>
                                <TableCell sx={{ ...tdBoldSx, color: '#ef4444' }}>{tr.var_99 != null ? `${(tr.var_99 * 100).toFixed(2)}%` : '--'}</TableCell>
                                <TableCell sx={{ ...tdBoldSx, color: '#dc2626' }}>{tr.cvar_95 != null ? `${(tr.cvar_95 * 100).toFixed(2)}%` : '--'}</TableCell>
                                <TableCell sx={{ ...tdBoldSx, color: '#dc2626' }}>{tr.cvar_99 != null ? `${(tr.cvar_99 * 100).toFixed(2)}%` : '--'}</TableCell>
                                <TableCell sx={{ ...tdBoldSx, color: '#64748b' }}>{tr.skewness != null ? tr.skewness.toFixed(4) : '--'}</TableCell>
                                <TableCell sx={{ ...tdSx, color: '#94a3b8', fontSize: '0.75rem' }}>{tr.estimation_date ? new Date(tr.estimation_date).toLocaleDateString() : '--'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination component="div" count={tailTotal} page={tailPage}
                onPageChange={(e, p) => setTailPage(p)} rowsPerPage={PER_PAGE} rowsPerPageOptions={[PER_PAGE]}
                sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
        </Paper>
    );

    // Permission guard
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view Market Regime Detection.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '16px',
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BubbleChartIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            Market Regime Detection
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            HMM regime detection, vol regimes, correlations, trend analysis, and tail risk
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Alerts */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

            {/* Action Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
                <Button startIcon={<RefreshIcon />} onClick={fetchSummary} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                    Refresh
                </Button>
                <Button
                    startIcon={runningAll ? <CircularProgress size={16} /> : <PlaylistPlayIcon />}
                    variant="contained" onClick={handleRunAll} disabled={runningAll} size="small"
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                    {runningAll ? 'Running Analysis...' : 'Run All Analysis'}
                </Button>
            </Box>

            {runningAll && <LinearProgress sx={{ mb: 2, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }} />}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
                    <Tab label="Regime Detection" icon={<BubbleChartIcon />} iconPosition="start" />
                    <Tab label="Vol Regime" icon={<ShowChartIcon />} iconPosition="start" />
                    <Tab label="Correlations" icon={<CompareArrowsIcon />} iconPosition="start" />
                    <Tab label="Trend / Reversion" icon={<TrendingUpIcon />} iconPosition="start" />
                    <Tab label="Tail Risk" icon={<WarningIcon />} iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {summaryLoading ? (
                <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
            ) : (
                <>
                    {activeTab === 0 && renderRegimeDetection()}
                    {activeTab === 1 && renderVolRegime()}
                    {activeTab === 2 && renderCorrelations()}
                    {activeTab === 3 && renderTrendReversion()}
                    {activeTab === 4 && renderTailRisk()}
                </>
            )}
        </Box>
    );
};

export default MarketRegimeDetectionPage;
